/**
 * Auth Store — manages authentication state.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import type { User, AuthResponse } from '../types/api';
import * as storage from '../utils/storage';
import * as api from '../api/client';
import { setSessionExpiredHandler } from '../api/client';
import { wsManager } from '../ws/WebSocketManager';
import { normalizeTagColorKeys } from '../utils/tagColors';
import { useOfflineQueue } from './offlineQueueStore';

function normalizeServerUser(user: User): User {
  return {
    ...user,
    tagColors: user.tagColors ? normalizeTagColorKeys(user.tagColors) : null,
  };
}

function normalizeUser(user: User | null | undefined): User | null {
  if (!user) return null;
  return normalizeServerUser(user);
}

function isAuthFailureError(error: unknown): boolean {
  const status = (error as any)?.response?.status;
  return status === 401 || status === 403;
}

function clearQueuedMutationsForUser(user: User | null | undefined): void {
  const ownerId = user?.waId?.trim();
  if (ownerId) useOfflineQueue.getState().clearForOwner(ownerId);
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  phone: string;

  // Actions
  setPhone: (phone: string) => void;
  requestOtp: (phone: string) => Promise<{ isTestAccount?: boolean }>;
  verifyOtp: (otp: string) => Promise<User>;
  loadSession: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  refreshProfile: () => Promise<void>;
  _forceLogout: () => void;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  phone: '',

  setPhone: (phone: string) => set({ phone }),

  requestOtp: async (phone: string) => {
    const normalized = phone.replace(/[^0-9+]/g, '');
    set({ phone: normalized });
    const result = await api.requestOtp(normalized);
    return { isTestAccount: result.isTestAccount };
  },

  verifyOtp: async (otp: string) => {
    const { phone } = get();
    const deviceId = await storage.getDeviceId();
    const platform = Platform.OS; // 'ios' | 'android' | 'web'

    const result = await api.verifyOtp({
      phone,
      otp,
      deviceId,
      platform,
    });

    await storage.setAccessToken(result.accessToken);
    await storage.setRefreshToken(result.refreshToken);
    const normalizedUser = normalizeServerUser(result.user);

    await storage.setUserData(normalizedUser);

    set({
      isAuthenticated: true,
      user: normalizedUser,
      isLoading: false,
    });

    // Connect WebSocket
    wsManager.connect();

    return normalizedUser;
  },

  loadSession: async () => {
    // Only a definitive refresh rejection reaches this handler. Transient
    // network/server failures keep the cached native session available.
    setSessionExpiredHandler(() => {
      wsManager.disconnect();
      void storage.clearAll().catch(() => {});
      set({ isAuthenticated: false, user: null, isLoading: false });
    });

    try {
      const token = await storage.getAccessToken();
      const refreshToken = await storage.getRefreshToken();
      const userData = normalizeUser(await storage.getUserData());
      const persistedUser = normalizeUser(get().user);

      if (Platform.OS === 'web' && !token && !refreshToken) {
        if (userData || persistedUser || get().isAuthenticated) {
          clearQueuedMutationsForUser(userData || persistedUser || get().user);
          await storage.clearAll().catch(() => {});
        }
        set({ isAuthenticated: false, user: null, isLoading: false });
        return;
      }

      if (token && userData) {
        // Restore session from cached data immediately
        set({
          isAuthenticated: true,
          user: userData,
          isLoading: false,
        });

        // Try to refresh profile from server (best-effort)
        try {
          const { user: serverUser } = await api.getUserProfile();
          const normalizedUser = normalizeServerUser(serverUser);
          await storage.setUserData(normalizedUser);
          set({ user: normalizedUser });
        } catch {
          // Profile refresh failed — continue with cached data.
          // If it was a 401 and the refresh token is truly invalid,
          // the interceptor's sessionExpiredHandler already handled logout.
        }

        // A rejected refresh may have invalidated the session while the
        // best-effort profile request was in flight.
        if (!get().isAuthenticated) return;

        // Connect WebSocket
        wsManager.connect();
      } else if (token) {
        // Have token but no cached user data — still authenticated,
        // just need to fetch profile
        set({ isAuthenticated: true, user: persistedUser || null, isLoading: false });
        try {
          const { user: serverUser } = await api.getUserProfile();
          const normalizedUser = normalizeServerUser(serverUser);
          await storage.setUserData(normalizedUser);
          set({ user: normalizedUser });
        } catch (error) {
          // On web, only explicit auth failures should force re-login.
          // Transient startup failures such as 429/offline should keep the
          // local session intact instead of dumping the user back to /phone.
          if (Platform.OS === 'web' && isAuthFailureError(error)) {
            clearQueuedMutationsForUser(get().user);
            await storage.clearAll().catch(() => {});
            set({ isAuthenticated: false, user: null, isLoading: false });
            return;
          }
        }
        if (!get().isAuthenticated) return;
        wsManager.connect();
      } else if (refreshToken || userData || persistedUser || get().isAuthenticated) {
        // Keep the user inside the app even if the server is offline or the
        // access token is missing after an app restart/update. Reconnect is best-effort.
        set({
          isAuthenticated: true,
          user: userData || persistedUser || null,
          isLoading: false,
        });

        if (refreshToken) {
          try {
            const refreshed = await api.refreshAccessToken(refreshToken);
            await storage.setAccessToken(refreshed.accessToken);
            if (refreshed.refreshToken) {
              await storage.setRefreshToken(refreshed.refreshToken);
            }
            try {
              const { user: serverUser } = await api.getUserProfile();
              const normalizedUser = normalizeServerUser(serverUser);
              await storage.setUserData(normalizedUser);
              set({ user: normalizedUser });
            } catch {
              // Keep using the cached profile.
            }
            wsManager.connect();
          } catch (error) {
            // refreshAccessToken() already triggers the session-expired handler
            // for real auth failures. For transient startup failures on web
            // (429/offline), keep the local session and retry later.
            if (isAuthFailureError(error)) {
              return;
            }
          }
        }
      } else {
        set({ isAuthenticated: false, isLoading: false });
      }
    } catch (err) {
      console.warn('[Auth] loadSession failed:', err);
      // Don't force logout on unexpected errors — preserve existing session
      const token = await storage.getAccessToken();
      const refreshToken = await storage.getRefreshToken();
      const secureUser = normalizeUser(await storage.getUserData().catch(() => null));
      if (Platform.OS === 'web' && !token && !refreshToken) {
        clearQueuedMutationsForUser(secureUser || get().user);
        await storage.clearAll().catch(() => {});
        set({ isAuthenticated: false, user: null, isLoading: false });
        return;
      }
      if (token || refreshToken || secureUser || get().user || get().isAuthenticated) {
        set({ isAuthenticated: true, user: secureUser || normalizeUser(get().user), isLoading: false });
        wsManager.connect();
      } else {
        set({ isAuthenticated: false, isLoading: false });
      }
    }
  },

  updateUser: (updates: Partial<User>) => {
    const current = get().user;
    if (current) {
      const updated = normalizeServerUser({
        ...current,
        ...updates,
        tagColors: updates.tagColors === undefined
          ? current.tagColors
          : updates.tagColors,
      });
      set({ user: updated });
      storage.setUserData(updated);
    }
  },

  refreshProfile: async () => {
    try {
      const { user: serverUser } = await api.getUserProfile();
      const normalizedUser = normalizeServerUser(serverUser);
      await storage.setUserData(normalizedUser);
      set({ user: normalizedUser });
    } catch {
      // Best-effort — keep using cached data
    }
  },

  /** Called by force_logout WS event — clears local state only (server already invalidated tokens) */
  _forceLogout: () => {
    clearQueuedMutationsForUser(get().user);
    set({ isAuthenticated: false, user: null, isLoading: false });
  },

  logout: async () => {
    let deviceId: string | undefined;
    try {
      deviceId = await storage.getDeviceId();
    } catch {
      deviceId = undefined;
    }

    // Disconnect WS and clear local state immediately — don't wait for server
    wsManager.disconnect();
    clearQueuedMutationsForUser(get().user);
    await storage.clearAll();
    set({ isAuthenticated: false, user: null, isLoading: false });

    // Best-effort server-side logout (fire and forget)
    try {
      api.logout(deviceId).catch(() => {});
    } catch {
      // Ignore
    }
  },
  }),
  {
    name: 'fm-auth',
    storage: createJSONStorage(() => AsyncStorage),
    partialize: (state) => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
    }),
  },
  ),
);
